import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLazyOnceFetch } from '../../utils/useLazyOnceFetch';
import {
  FILE_CHIPS, KIND_LABEL, PAGE_SIZE, chipByKey, chipCount, visibleChips, safeHref, youtubeThumb, whenLabel,
  fetchRoomArtifacts, fetchRoomArtifactsPage, fetchAllRoomArtifacts, artifactsToMarkdown, artifactsFileName, downloadText,
} from '../../utils/roomArtifacts';

// 🗂️ FILES, the manila folder (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §5.7, mockup
// room-toolkit.html §2 option A, 09-29). Every image, doc, drawing, note, code
// card and toy the room shared, newest first, with filter chips, open-in-a-new-
// tab links and "Download list (.md)". A read, not new storage: the backend's
// `getRoomArtifacts` (src/ChatGame/roomArtifacts.js) pages the room's existing
// shared_content rows; nothing here writes.
//
// MOUNT CONTRACT (self-contained: it portals to <body>, so it can sit anywhere):
//   open        boolean. Closed = nothing rendered, nothing fetched.
//   onClose     () => void. ✕, Esc and the scrim call it.
//   gameId      the room id.
//   viewer      { player_id, player_secret } of THIS viewer's seat, or null.
//               A private port refuses without it (the proof rides a POST body).
//   THEME       optional; only THEME.accent is read (the focus ring).
//   api         optional { list(opts) } with fetchRoomArtifacts' contract (tests,
//               previews). Read through a ref: an inline object is fine.
//   roomName    optional header name (else the server's room name).
//   isMobile    optional; default = window width < 768. Phones get a
//               full-height sheet, desktop a right-edge drawer.
//   topOffset   optional px the desktop drawer starts below (a top bar). 0.
//   zIndex      optional; default 150010 (above the phone ghost sheet, 150000).
//   onOpenItem  optional (item) => void for rows with no link (code, toys,
//               notes): e.g. jump to the card in chat. Absent = the row opens
//               in place to show its line.
//
// Chrome: the dossier's manila (#eed9a4 → #dcc07c, #b28f4a edge), the mockup's
// ink/muted/line tokens. Class names avoid every substring generated themes
// target ([class*='card'|'panel'|'chip'|'message'|'btn'|'button'|'badge'|'tag'|
// 'user'|'player'|'header'|'popover'|'tooltip']); buttons carry data-prop-ui and
// their own !important armor (the PortedMenu idiom). No global border-box here:
// every box sets its own.

const INK = '#3A2E1B';
const MUTED = '#6E5C3C';
const LINE = 'rgba(58,46,27,0.20)';
const CARD = '#F4E8CB';
const MANILA_2 = '#D8BC82';

const CSS = `
.mfo-root, .mfo-root * { box-sizing: border-box; }
.mfo-scrim { position: fixed; inset: 0; background: rgba(12,9,6,0.34); animation: mfoFade .16s ease-out; }
.mfo-root { position: fixed; display: flex; flex-direction: column; color: ${INK}; text-align: left; text-shadow: none;
  background: radial-gradient(120% 60% at 15% 0%, rgba(255,248,226,.55), transparent 55%), linear-gradient(180deg, #eed9a4 0%, #dcc07c 100%);
  border: 1px solid #b28f4a; box-shadow: -8px 10px 28px rgba(0,0,0,0.45);
  font: 400 14px/1.35 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; }
.mfo-root.mfo-desk { right: 0; bottom: 0; width: min(476px, calc(100vw - 24px)); border-right: none; border-radius: 12px 0 0 12px; animation: mfoSlide .2s ease-out; }
.mfo-root.mfo-phone { left: 0; right: 0; bottom: 0; top: max(8px, env(safe-area-inset-top)); border-radius: 16px 16px 0 0; border-bottom: none; animation: mfoRise .22s ease-out; }
.mfo-flap { position: absolute; left: -40px; top: 24px; width: 40px; height: 112px; border-radius: 10px 0 0 10px; background: linear-gradient(90deg, #cdaa66, #e3cb98); border: 1px solid #b28f4a; border-right: none; display: grid; place-items: center; color: ${INK}; pointer-events: none; }
.mfo-flap span { writing-mode: vertical-rl; transform: rotate(180deg); font: 600 11px/1 ui-monospace, "SF Mono", Menlo, monospace; letter-spacing: .16em; }
.mfo-grab { width: 44px; height: 5px; border-radius: 3px; background: ${LINE}; margin: 8px auto 0; flex: none; }
.mfo-clip { position: absolute; top: -8px; right: 74px; color: ${MUTED}; pointer-events: none; }
.mfo-top { display: flex; align-items: flex-start; gap: 12px; padding: 20px 22px 10px; flex: none; }
.mfo-phone .mfo-top { padding: 12px 16px 8px; }
.mfo-root .mfo-ttl.mfo-ttl { margin: 0 !important; padding: 0 !important; font: 700 25px/1.1 "Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif !important; letter-spacing: -.01em !important; color: ${INK} !important; text-shadow: none !important; text-transform: none !important; }
.mfo-sub { margin-top: 4px; font: 500 11px/1.3 ui-monospace, "SF Mono", Menlo, monospace; letter-spacing: .08em; text-transform: uppercase; color: ${MUTED}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mfo-filters { display: flex; flex-wrap: wrap; gap: 6px; padding: 4px 22px 12px; flex: none; }
.mfo-phone .mfo-filters { padding: 2px 16px 10px; flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; }
.mfo-list { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; margin: 0; padding: 2px 18px 12px; list-style: none; display: flex; flex-direction: column; gap: 8px; }
.mfo-phone .mfo-list { padding: 2px 12px 12px; }
.mfo-root .mfo-list > li { margin: 0 !important; padding: 0 !important; list-style: none !important; }
.mfo-row { display: grid; grid-template-columns: 58px minmax(0, 1fr) auto; gap: 12px; align-items: center; width: 100%; min-height: 60px; padding: 8px 10px 8px 8px; border-radius: 8px; background: ${CARD}; box-shadow: 0 1px 0 ${LINE}, inset 0 0 0 1px ${LINE}; color: ${INK} !important; text-decoration: none !important; text-align: left; }
a.mfo-row:hover, .mfo-row.mfo-press:hover { box-shadow: 0 1px 0 ${LINE}, inset 0 0 0 1px rgba(58,46,27,0.42); }
.mfo-th { width: 58px; height: 44px; border-radius: 5px; overflow: hidden; display: grid; place-items: center; background: ${MANILA_2}; color: ${INK}; font: 700 10px/1 ui-monospace, "SF Mono", Menlo, monospace; letter-spacing: .06em; }
.mfo-th img { width: 100%; height: 100%; object-fit: cover; display: block; }
.mfo-glyph { font-size: 18px; line-height: 1; }
.mfo-txt { min-width: 0; }
.mfo-nm { font-weight: 600; font-size: 13.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mfo-meta { font-size: 12px; color: ${MUTED}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 2px; }
.mfo-line { font-size: 12.5px; color: ${INK}; margin-top: 6px; white-space: normal; overflow-wrap: anywhere; }
.mfo-kind { font: 500 10px/1 ui-monospace, "SF Mono", Menlo, monospace; letter-spacing: .08em; text-transform: uppercase; color: ${MUTED}; padding: 3px 6px; border-radius: 4px; box-shadow: inset 0 0 0 1px ${LINE}; white-space: nowrap; }
.mfo-say { margin: 18px 6px; font-size: 14px; color: ${MUTED}; text-align: center; }
.mfo-foot { flex: none; padding: 12px 18px 16px; display: grid; gap: 10px; border-top: 1px dashed ${LINE}; }
.mfo-phone .mfo-foot { padding: 10px 12px calc(12px + env(safe-area-inset-bottom)); }
.mfo-hint { font-size: 12px; color: ${MUTED}; display: flex; gap: 6px; align-items: center; margin: 0; }
.mfo-err { font-size: 12px; color: #8a2a12; margin: 0; }
/* the armor: generated themes set button padding/border/font/shadow/background with !important */
.mfo-root .mfo-x.mfo-x, .mfo-root .mfo-filt.mfo-filt, .mfo-root .mfo-dl.mfo-dl, .mfo-root .mfo-more.mfo-more, .mfo-root .mfo-row.mfo-press {
  box-sizing: border-box !important; margin: 0 !important; text-shadow: none !important; transform: none !important; min-width: 0 !important; min-height: 0 !important; max-width: none !important; text-transform: none !important; letter-spacing: normal !important; cursor: pointer; }
.mfo-root .mfo-x.mfo-x { flex: none; width: 32px !important; height: 32px !important; padding: 0 !important; display: grid; place-items: center; border: 0 !important; border-radius: 9px !important; box-shadow: inset 0 0 0 1px ${LINE} !important; color: ${INK} !important; font: 400 18px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; }
.mfo-root .mfo-filt.mfo-filt { flex: none; width: auto !important; height: auto !important; padding: 5px 10px !important; border: 0 !important; border-radius: 7px !important; box-shadow: inset 0 0 0 1px ${LINE} !important; color: ${INK} !important; font: 600 12.5px/1.2 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; white-space: nowrap; }
.mfo-root .mfo-filt.mfo-filt[aria-pressed="true"] { color: ${CARD} !important; box-shadow: none !important; }
.mfo-filt .mfo-ct { opacity: .65; margin-left: 4px; font: 500 10.5px/1 ui-monospace, "SF Mono", Menlo, monospace; }
.mfo-root .mfo-dl.mfo-dl { width: 100% !important; height: 40px !important; padding: 0 12px !important; border: 0 !important; border-radius: 10px !important; box-shadow: none !important; color: ${CARD} !important; font: 700 13.5px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; display: inline-flex; align-items: center; justify-content: center; gap: 7px; }
.mfo-root .mfo-dl.mfo-dl:disabled { opacity: .6; cursor: default; }
.mfo-root .mfo-more.mfo-more { align-self: center; width: auto !important; height: auto !important; padding: 7px 14px !important; border: 0 !important; border-radius: 999px !important; box-shadow: inset 0 0 0 1.5px ${INK} !important; color: ${INK} !important; font: 600 13px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; }
.mfo-root .mfo-row.mfo-press { width: 100% !important; height: auto !important; min-height: 60px !important; padding: 8px 10px 8px 8px !important; border: 0 !important; border-radius: 8px !important; font: inherit !important; }
.mfo-root .mfo-x:focus-visible, .mfo-root .mfo-filt:focus-visible, .mfo-root .mfo-dl:focus-visible, .mfo-root .mfo-more:focus-visible, .mfo-root .mfo-row:focus-visible { outline: 2px solid var(--mfo-ring, #8a5a1c) !important; outline-offset: 2px; }
@keyframes mfoSlide { from { transform: translateX(24px); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes mfoRise { from { transform: translateY(32px); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes mfoFade { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .mfo-root, .mfo-scrim { animation: none; } }
`;

const EMPTY_VIEW = Object.freeze({ loading: false, loaded: false, items: [], hasMore: false, next: null, error: null });
const FAILED_VIEW = Object.freeze({ loading: false, loaded: true, items: [], hasMore: false, next: null, error: 'failed' });

// Module level on purpose: useLazyOnceFetch's loaders must be stable.
const viewFromPage = (r) => (r && r.ok
  ? { loading: false, loaded: true, items: r.items, hasMore: r.hasMore, next: r.next, error: null, counts: r.counts || null, countsCapped: !!r.countsCapped, roomName: r.roomName || null }
  : { ...FAILED_VIEW, error: r && r.private ? 'private' : 'failed' });

const DOC_LABEL = { pdf: 'PDF', docx: 'DOC', doc: 'DOC', txt: 'TXT', md: 'MD', csv: 'CSV' };
const GLYPH = { video: '▶', note: '✎', link: '↗', toy: '🧸' };

function Thumb({ item }) {
  const [broken, setBroken] = useState(false);
  const src = (item.kind === 'image' || item.kind === 'drawing') ? safeHref(item.url)
    : (item.kind === 'video' ? youtubeThumb(item.url) : null);
  if (src && !broken) {
    return <span className="mfo-th"><img src={src} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={() => setBroken(true)} /></span>;
  }
  if (item.kind === 'document') return <span className="mfo-th">{DOC_LABEL[item.format] || (item.format ? String(item.format).toUpperCase().slice(0, 4) : 'DOC')}</span>;
  if (item.kind === 'code') return <span className="mfo-th">{'</>'}</span>;
  if (item.kind === 'image' || item.kind === 'drawing') return <span className="mfo-th"><span className="mfo-glyph" aria-hidden="true">{item.kind === 'drawing' ? '✏' : '▦'}</span></span>;
  return <span className="mfo-th"><span className="mfo-glyph" aria-hidden="true">{GLYPH[item.kind] || '•'}</span></span>;
}

function Row({ item, now, expanded, onToggle, onOpenItem }) {
  const href = safeHref(item.url);
  const meta = [item.sender_name, whenLabel(item.at, now)].filter(Boolean).join(' · ');
  const kind = item.comic ? 'Comic' : (KIND_LABEL[item.kind] || 'File');
  const inner = (
    <>
      <Thumb item={item} />
      <span className="mfo-txt">
        <span className="mfo-nm" style={{ display: 'block' }}>{item.title}</span>
        <span className="mfo-meta" style={{ display: 'block' }}>{meta}</span>
        {!href && expanded && item.summary ? <span className="mfo-line" style={{ display: 'block' }}>{item.summary}</span> : null}
      </span>
      <span className="mfo-kind">{kind}</span>
    </>
  );
  if (href) {
    return (
      <a className="mfo-row" href={href} target="_blank" rel="noopener noreferrer" title={item.summary || item.title} data-file-id={item.id}>
        {inner}
      </a>
    );
  }
  return (
    <button
      type="button"
      className="mfo-row mfo-press"
      data-prop-ui=""
      style={{ '--prop-bg': CARD, background: CARD }}
      aria-expanded={onOpenItem ? undefined : expanded}
      data-file-id={item.id}
      onClick={() => (onOpenItem ? onOpenItem(item) : onToggle(item.id))}
    >
      {inner}
    </button>
  );
}

export default function FilesFolder({
  open = false, onClose, gameId, viewer = null, THEME = null, api = null, roomName = null,
  isMobile, topOffset = 0, zIndex = 150010, onOpenItem = null,
}) {
  const phone = typeof isMobile === 'boolean' ? isMobile : (typeof window !== 'undefined' && window.innerWidth < 768);
  const titleId = `mfo-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [chip, setChip] = useState('all');
  const [views, setViews] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());
  const [dl, setDl] = useState('idle'); // idle | busy | error
  const closeRef = useRef(null);
  const rootRef = useRef(null);

  // Refs, so the loaders stay stable (a new function every render would restart
  // the fetch every render: useLazyOnceFetch's contract).
  const apiRef = useRef(api); apiRef.current = api;
  const viewerRef = useRef(viewer); viewerRef.current = viewer;
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  const close = useCallback(() => { if (onCloseRef.current) onCloseRef.current(); }, []);
  const listFn = useCallback((opts) => ((apiRef.current && apiRef.current.list) || fetchRoomArtifacts)(opts), []);

  const viewerId = (viewer && (viewer.player_id || viewer.playerId)) || '';
  const keyOf = (c) => (gameId ? `${gameId}|${c}|${viewerId}` : '');
  const key = keyOf(chip);
  const allKey = keyOf('all');
  const view = views[key] || EMPTY_VIEW;
  const allView = views[allKey] || EMPTY_VIEW;

  const keyRef = useRef(key); keyRef.current = key;
  const setView = useCallback((u) => {
    const k = keyRef.current;
    setViews((all) => {
      const prev = all[k] || EMPTY_VIEW;
      return { ...all, [k]: typeof u === 'function' ? u(prev) : u };
    });
  }, []);
  const load = useCallback((k) => {
    const [g, c] = String(k).split('|');
    const ch = chipByKey(c);
    return fetchRoomArtifactsPage({ gameId: g, viewer: viewerRef.current, kinds: ch.kinds, limit: PAGE_SIZE, counts: !ch.kinds }, listFn);
  }, [listFn]);

  useLazyOnceFetch({ wanted: !!open, key, loaded: view.loaded, setState: setView, load, toState: viewFromPage, failState: FAILED_VIEW });

  // Esc closes; focus lands on ✕ at open and goes back where it was at close
  // (keyed on `open` alone: an inline onClose must not re-run this each render).
  // Tab stays inside (the dialog says aria-modal: Tab must not walk into the
  // room behind the scrim).
  useEffect(() => {
    if (!open) return undefined;
    const back = typeof document !== 'undefined' ? document.activeElement : null;
    if (closeRef.current) closeRef.current.focus({ preventScroll: true });
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(); return; }
      const root = rootRef.current;
      if (e.key !== 'Tab' || !root) return;
      const stops = [...root.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')];
      if (!stops.length) return;
      const first = stops[0];
      const last = stops[stops.length - 1];
      const at = document.activeElement;
      const inside = root.contains(at);
      if (e.shiftKey && (at === first || !inside)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (at === last || !inside)) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (back && typeof back.focus === 'function') { try { back.focus({ preventScroll: true }); } catch (e) { /* gone */ } }
    };
  }, [open, close]);

  // Each open reads the folder fresh (a file shared since the last look shows up).
  useEffect(() => {
    if (open) return;
    setViews({});
    setExpanded(new Set());
    setDl('idle');
  }, [open]);

  const loadMore = async () => {
    const k = key;
    const v = views[k];
    if (!v || !v.hasMore || !v.next || v.more) return;
    const ch = chipByKey(chip);
    setViews((all) => ({ ...all, [k]: { ...all[k], more: true, moreError: false } }));
    let r = null;
    try {
      r = await fetchRoomArtifactsPage({ gameId, viewer: viewerRef.current, kinds: ch.kinds, limit: PAGE_SIZE, before: v.next }, listFn);
    } catch (e) { r = null; }
    setViews((all) => {
      const cur = all[k];
      if (!cur) return all;
      if (!r || !r.ok) return { ...all, [k]: { ...cur, more: false, moreError: true } };
      const seen = new Set(cur.items.map((it) => it.id));
      return { ...all, [k]: { ...cur, more: false, items: [...cur.items, ...r.items.filter((it) => !seen.has(it.id))], hasMore: r.hasMore, next: r.next } };
    });
  };

  const retry = () => setViews((all) => { const next = { ...all }; delete next[key]; return next; });

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const name = roomName || allView.roomName || view.roomName || '';
  const download = async () => {
    if (dl === 'busy') return;
    setDl('busy');
    try {
      const { items, capped, roomName: serverName } = await fetchAllRoomArtifacts({ gameId, viewer: viewerRef.current }, listFn);
      const room = name || serverName || '';
      const now = new Date();
      downloadText(artifactsFileName(room, now), artifactsToMarkdown(room, items, { now, capped }));
      setDl('idle');
    } catch (e) {
      setDl('error');
    }
  };

  if (!open || typeof document === 'undefined') return null;

  const counts = allView.counts || null;
  const capped = !!allView.countsCapped;
  const shown = visibleChips(counts, capped);
  const chips = FILE_CHIPS.filter((c) => c.key === chip || shown.includes(c)); // the chip you are on never vanishes
  const total = chipCount(FILE_CHIPS[0], counts);
  const sub = [name, total == null ? null : `${total}${capped ? '+' : ''} file${total === 1 && !capped ? '' : 's'}`].filter(Boolean).join(' · ');
  const now = new Date();
  const ring = (THEME && typeof THEME.accent === 'string' && THEME.accent) || '#8a5a1c';
  const current = chipByKey(chip);

  let body;
  if (!gameId) {
    body = <p className="mfo-say">Nothing shared yet. Drop a file in the chat and it lands here.</p>;
  } else if (!view.loaded) {
    body = <p className="mfo-say" role="status">Opening the folder…</p>;
  } else if (view.error === 'private') {
    body = <p className="mfo-say">Only people in this room can open its files.</p>;
  } else if (view.error) {
    body = (
      <div style={{ display: 'grid', justifyItems: 'center', gap: 8, margin: '18px 0' }}>
        <p className="mfo-say" style={{ margin: 0 }}>Couldn’t open the folder.</p>
        <button type="button" className="mfo-more" data-prop-ui="" onClick={retry}>Try again</button>
      </div>
    );
  } else if (!view.items.length) {
    body = (
      <p className="mfo-say">
        {current.kinds ? `No ${current.label.toLowerCase()} yet.` : 'Nothing shared yet. Drop a file in the chat and it lands here.'}
      </p>
    );
  } else {
    body = null;
  }

  return createPortal(
    <>
      <style>{CSS}</style>
      <div className="mfo-scrim" style={{ zIndex: zIndex - 1 }} onClick={close} aria-hidden="true" data-files-scrim="" />
      <div
        ref={rootRef}
        className={`mfo-root ${phone ? 'mfo-phone' : 'mfo-desk'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-files-folder=""
        style={{ zIndex, '--mfo-ring': ring, ...(phone ? {} : { top: Math.max(0, Number(topOffset) || 0) }) }}
      >
        {phone ? <div className="mfo-grab" aria-hidden="true" /> : <div className="mfo-flap" aria-hidden="true"><span>FILES</span></div>}
        {phone ? null : (
          <svg className="mfo-clip" width="22" height="46" viewBox="0 0 22 46" aria-hidden="true">
            <path d="M7 16V8a4 4 0 0 1 8 0v26a6 6 0 0 1-12 0V12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          </svg>
        )}
        <div className="mfo-top">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="mfo-ttl" id={titleId}>Files</h2>
            {sub ? <div className="mfo-sub">{sub}</div> : null}
          </div>
          <button ref={closeRef} type="button" className="mfo-x" data-prop-ui="" aria-label="Close files" onClick={close}>✕</button>
        </div>
        <div className="mfo-filters" role="group" aria-label="Show">
          {chips.map((c) => {
            const on = c.key === chip;
            const n = chipCount(c, counts);
            return (
              <button
                key={c.key}
                type="button"
                className="mfo-filt"
                data-prop-ui=""
                data-files-filter={c.key}
                aria-pressed={on}
                style={{ '--prop-bg': on ? INK : 'transparent', background: on ? INK : 'transparent' }}
                onClick={() => setChip(c.key)}
              >
                {c.label}{n == null ? null : <span className="mfo-ct">{n}{capped && !c.kinds ? '+' : ''}</span>}
              </button>
            );
          })}
        </div>
        {body ? <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{body}</div> : (
          <ul className="mfo-list" aria-label={`${current.label} files`}>
            {view.items.map((it) => (
              <li key={it.id}>
                <Row item={it} now={now} expanded={expanded.has(it.id)} onToggle={toggle} onOpenItem={onOpenItem} />
              </li>
            ))}
            {view.hasMore ? (
              <li style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, paddingTop: 4 }}>
                <button type="button" className="mfo-more" data-prop-ui="" onClick={loadMore} disabled={!!view.more}>
                  {view.more ? 'Looking…' : 'Older files'}
                </button>
                {view.moreError ? <p className="mfo-err">Couldn’t load more. Try again.</p> : null}
              </li>
            ) : null}
          </ul>
        )}
        <div className="mfo-foot">
          <button
            type="button"
            className="mfo-dl"
            data-prop-ui=""
            style={{ '--prop-bg': INK, background: INK }}
            onClick={download}
            disabled={dl === 'busy' || view.error === 'private'}
          >
            <span aria-hidden="true">↓</span>{dl === 'busy' ? 'Saving…' : 'Download list (.md)'}
          </button>
          {dl === 'error' ? <p className="mfo-err" role="status">Couldn’t save the list. Try again.</p> : null}
          <p className="mfo-hint">Anyone in the room can open these. Links are hard to guess, not secret.</p>
        </div>
      </div>
    </>,
    document.body,
  );
}
